import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { AgentDto, LlmModelDto, LlmProviderDto } from '@shared/services/apiService/apiService';

import { resolveModelTitle, resolveProviderTitle } from '../helpers/llmDisplay';

export type LlmCompositeCellProps = {
  row: AgentDto;
  llmProviders?: LlmProviderDto[];
  providerModelsByProviderId?: Record<string, LlmModelDto[] | undefined>;
};

/**
 * Displays LLM provider + model in a single compact cell.
 * - Provider title on first line
 * - Model title or custom key on second line
 */
export const LlmCompositeCell: React.FC<LlmCompositeCellProps> = ({
  row,
  llmProviders,
  providerModelsByProviderId,
}) => {
  const { t } = useTranslation();

  const providerTitle = resolveProviderTitle(row, llmProviders, t);

  const providerModels = row.llmProviderId
    ? providerModelsByProviderId?.[row.llmProviderId]
    : undefined;

  const modelTitle = resolveModelTitle(row, llmProviders, providerModels, t);
  const tooltipTitle = `${providerTitle} / ${modelTitle}`;

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <Box sx={{ minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle1" noWrap sx={{ minWidth: 0, maxWidth: '100%' }}>
          {providerTitle}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ display: 'block', minWidth: 0, maxWidth: '100%' }}>
          {modelTitle}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default LlmCompositeCell;
